import { createGame, getPreset, type IconId } from '@ic/engine';
import { AccountSection, AdminSection } from '../components/AdminTools';
import { Board } from '../components/Board';
import { ChoiceCards, Toggle } from '../components/Controls';
import { Icon } from '../components/Icons';
import { PieceIcon } from '../components/Pieces';
import { settings, useStore, type Settings } from '../lib/store';

const preview = (() => {
  const rs = getPreset('illegal');
  rs.board = { width: 6, height: 3, voids: [], walls: [] };
  rs.rules.check = false;
  rs.setup = [
    { sq: 'a1', type: 'dragon', color: 'w' }, { sq: 'b1', type: 'bomb', color: 'w' }, { sq: 'c1', type: 'miner', color: 'w' },
    { sq: 'd1', type: 'king', color: 'w' }, { sq: 'e1', type: 'queen', color: 'w' }, { sq: 'f1', type: 'knight', color: 'w' },
    { sq: 'a3', type: 'rook', color: 'b' }, { sq: 'b3', type: 'bishop', color: 'b' }, { sq: 'c3', type: 'pawn', color: 'b' },
    { sq: 'd3', type: 'dragon', color: 'b' }, { sq: 'e3', type: 'bomb', color: 'b' }, { sq: 'f3', type: 'miner', color: 'b' },
  ];
  return createGame(rs);
})();

const BOARDS: { value: Settings['boardTheme']; title: string; light: string; dark: string }[] = [
  { value: 'sand', title: 'Sand', light: '#eee2cf', dark: '#bf8f6a' },
  { value: 'walnut', title: 'Walnut', light: '#f0d9b5', dark: '#b58863' },
  { value: 'emerald', title: 'Emerald', light: '#e9e6cf', dark: '#6f8f62' },
  { value: 'ice', title: 'Ice', light: '#e3e7e8', dark: '#8a9fa8' },
  { value: 'neon', title: 'Neon', light: '#241a44', dark: '#150f2b' },
];

function Swatch({ light, dark }: { light: string; dark: string }) {
  return <span className="swatch">{[light, dark, dark, light].map((c, i) => <i key={i} style={{ background: c }} />)}</span>;
}

function Duo({ set, icons }: { set: Settings['pieceSet']; icons: [IconId, IconId] }) {
  return (
    <span className={`duo ${set === 'neon' ? 'night' : ''}`}>
      <PieceIcon icon={icons[0]} color="w" set={set} />
      <PieceIcon icon={icons[1]} color="b" set={set} />
    </span>
  );
}

export function SettingsPage() {
  const s = useStore(settings);
  const set = (patch: Partial<Settings>) => settings.set({ ...s, ...patch });
  return (
    <div className="page narrow">
      <h1>Settings</h1>
      <div className="settings-grid">
        <section>
          <h2>Appearance</h2>
          <ChoiceCards<Settings['theme']>
            className="compact"
            value={s.theme}
            onChange={(theme) => set({ theme })}
            options={[
              { value: 'system', title: 'System', visual: <Icon name="monitor" size={24} /> },
              { value: 'light', title: 'Light', visual: <Icon name="sun" size={24} /> },
              { value: 'dark', title: 'Dark', visual: <Icon name="moon" size={24} /> },
            ]}
          />
        </section>
        <section>
          <h2>Pieces</h2>
          <ChoiceCards<Settings['pieceSet']>
            className="compact"
            value={s.pieceSet}
            onChange={(pieceSet) => set({ pieceSet })}
            options={[
              { value: 'classic', title: 'Classic', visual: <Duo set="classic" icons={['king', 'dragon']} /> },
              { value: 'neon', title: 'Neon', visual: <Duo set="neon" icons={['king', 'dragon']} /> },
            ]}
          />
        </section>
        <section>
          <h2>Board</h2>
          <ChoiceCards<Settings['boardTheme']>
            className="compact"
            value={s.boardTheme}
            onChange={(boardTheme) => set({ boardTheme })}
            options={BOARDS.map((b) => ({ value: b.value, title: b.title, visual: <Swatch light={b.light} dark={b.dark} /> }))}
          />
          <div className="settings-preview"><Board state={preview} orientation="w" /></div>
        </section>
        <section>
          <h2>Gameplay</h2>
          <div className="section-card">
            <Toggle checked={s.sound} onChange={(sound) => set({ sound })} label="Sounds" hint="Moves, captures, explosions and fire." />
            <Toggle checked={s.animations} onChange={(animations) => set({ animations })} label="Animations" hint="Slide pieces and play ability effects." />
            <Toggle checked={s.coords} onChange={(coords) => set({ coords })} label="Coordinates" hint="Show files and ranks on the board edge." />
            <Toggle checked={s.moveFeedback} onChange={(moveFeedback) => set({ moveFeedback })} label="Move ratings" hint="Label moves as brilliant, best, inaccuracy, blunder… while you play." />
          </div>
        </section>
        <AccountSection />
        <AdminSection />
      </div>
    </div>
  );
}
